import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  FaEdit,
  FaLink,
  FaMoneyBillWave,
  FaPlus,
  FaSyncAlt,
  FaUndoAlt,
} from "react-icons/fa";

import {
  getResumenesPeajes,
  crearResumenPeaje,
  editarResumenPeaje,
  getConsumosDisponiblesPeajes,
  getConsumosResumenPeaje,
  vincularConsumosResumenPeaje,
  pagarResumenPeaje,
  anularPagoResumenPeaje,
} from "../helpers/rutaPeajes";

import {
  getCajas,
} from "../helpers/rutaCajas";

import {
  getCuentas,
} from "../helpers/rutaCuentasBancarias";

import {
  getCarteraDisponibles,
} from "../helpers/rutaCarteras";

/* =========================================================
   HELPERS
========================================================= */

const money = (valor) =>
  Number(valor || 0).toLocaleString(
    "es-AR",
    {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

const fechaCorta = (valor) => {
  if (!valor) return "-";

  try {
    return new Date(valor).toLocaleDateString(
      "es-AR",
      {
        timeZone: "UTC",
      }
    );
  } catch {
    return "-";
  }
};

const fechaInput = (valor) => {
  if (!valor) return "";

  try {
    return new Date(valor)
      .toISOString()
      .slice(0, 10);
  } catch {
    return "";
  }
};

const hoyInput = () =>
  new Date().toISOString().slice(0, 10);

const n = (valor) => {
  const numero = Number(valor);

  return Number.isFinite(numero)
    ? numero
    : 0;
};

const getCajaLabel = (caja) => {
  if (!caja) return "";

  return (
    caja.descripcion ||
    caja.caja ||
    caja.nombre ||
    `Caja ${caja.codcaj || ""}`
  );
};

const getCuentaLabel = (cuenta) => {
  if (!cuenta) return "";

  return [
    cuenta.alias,
    cuenta.banco,
    cuenta.numero,
  ]
    .filter(Boolean)
    .join(" - ");
};

const getVehiculo = (consumo) => {
  if (!consumo) return "-";

  const vehiculo =
    consumo.vehiculo;

  if (
    vehiculo &&
    typeof vehiculo === "object"
  ) {
    return (
      vehiculo.patente ||
      vehiculo.camion ||
      consumo.patente ||
      "-"
    );
  }

  return consumo.patente || "-";
};

const normalizarItemsCartera = (
  respuesta
) => {
  if (Array.isArray(respuesta)) {
    return respuesta;
  }

  if (
    Array.isArray(
      respuesta?.items
    )
  ) {
    return respuesta.items;
  }

  if (
    Array.isArray(
      respuesta?.cartera
    )
  ) {
    return respuesta.cartera;
  }

  if (
    Array.isArray(
      respuesta?.elementos
    )
  ) {
    return respuesta.elementos;
  }

  return [];
};

const estadoBadge = (estado) => {
  switch (
    String(estado || "")
      .toUpperCase()
  ) {
    case "PAGADO":
      return "success";

    case "VENCIDO":
      return "danger";

    case "ANULADO":
      return "secondary";

    default:
      return "warning";
  }
};

/* =========================================================
   FORMULARIO VACÍO
========================================================= */

const FORM_INICIAL = {
  proveedor: "TELEPASE",

  tipoDocumento:
    "FACTURA",

  concesionario: "",

  nroDocumento: "",

  fechaEmision:
    hoyInput(),

  periodoDesde: "",

  periodoHasta: "",

  subtotalConsumos: "",

  impuestosPercepciones:
    "",

  otrosConceptos: "",

  saldoAnterior: "",

  pagosPeriodo: "",

  saldoPendienteAnterior:
    "",

  total: "",

  fechaVencimiento1: "",
  importeVencimiento1: "",

  fechaVencimiento2: "",
  importeVencimiento2: "",

  observaciones: "",
};

/* =========================================================
   MODAL ALTA / EDICIÓN
========================================================= */

function ModalResumen({
  show,
  onHide,
  resumen,
  onSaved,
}) {
  const editando =
    !!resumen?._id;

  const [form, setForm] =
    useState(FORM_INICIAL);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  useEffect(() => {
    if (!show) return;

    setError("");

    if (!resumen) {
      setForm({
        ...FORM_INICIAL,
        fechaEmision:
          hoyInput(),
      });

      return;
    }

    setForm({
      proveedor:
        resumen.proveedor ||
        "TELEPASE",

      tipoDocumento:
        resumen.tipoDocumento ||
        "FACTURA",

      concesionario:
        resumen.concesionario ||
        "",

      nroDocumento:
        resumen.nroDocumento ||
        "",

      fechaEmision:
        fechaInput(
          resumen.fechaEmision
        ),

      periodoDesde:
        fechaInput(
          resumen.periodoDesde
        ),

      periodoHasta:
        fechaInput(
          resumen.periodoHasta
        ),

      subtotalConsumos:
        resumen.subtotalConsumos ??
        "",

      impuestosPercepciones:
        resumen.impuestosPercepciones ??
        "",

      otrosConceptos:
        resumen.otrosConceptos ??
        "",

      saldoAnterior:
        resumen.saldoAnterior ??
        "",

      pagosPeriodo:
        resumen.pagosPeriodo ??
        "",

      saldoPendienteAnterior:
        resumen.saldoPendienteAnterior ??
        "",

      total:
        resumen.total ?? "",

      fechaVencimiento1:
        fechaInput(
          resumen.vencimientos?.[0]
            ?.fecha
        ),

      importeVencimiento1:
        resumen.vencimientos?.[0]
          ?.importe ?? "",

      fechaVencimiento2:
        fechaInput(
          resumen.vencimientos?.[1]
            ?.fecha
        ),

      importeVencimiento2:
        resumen.vencimientos?.[1]
          ?.importe ?? "",

      observaciones:
        resumen.observaciones ||
        "",
    });
  }, [show, resumen]);

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const calcularTotal = () => {
    const resultado =
      n(form.subtotalConsumos) +
      n(
        form.impuestosPercepciones
      ) +
      n(form.otrosConceptos) +
      n(
        form.saldoPendienteAnterior
      );

    setForm((prev) => ({
      ...prev,
      total:
        Number(
          resultado.toFixed(2)
        ),
    }));
  };

  const guardar = async (e) => {
    e.preventDefault();

    setError("");

    if (
      !form.nroDocumento.trim()
    ) {
      setError(
        "Debe ingresar el número de factura/resumen."
      );
      return;
    }

    if (
      n(form.total) <= 0
    ) {
      setError(
        "El total debe ser mayor a cero."
      );
      return;
    }

    const payload = {
      proveedor:
        form.proveedor,

      tipoDocumento:
        form.tipoDocumento,

      concesionario:
        form.concesionario,

      nroDocumento:
        form.nroDocumento.trim(),

      fechaEmision:
        form.fechaEmision ||
        null,

      periodoDesde:
        form.periodoDesde ||
        null,

      periodoHasta:
        form.periodoHasta ||
        null,

      subtotalConsumos:
        n(
          form.subtotalConsumos
        ),

      impuestosPercepciones:
        n(
          form.impuestosPercepciones
        ),

      otrosConceptos:
        n(
          form.otrosConceptos
        ),

      saldoAnterior:
        n(form.saldoAnterior),

      pagosPeriodo:
        n(form.pagosPeriodo),

      saldoPendienteAnterior:
        n(
          form.saldoPendienteAnterior
        ),

      total:
        n(form.total),

      observaciones:
        form.observaciones,

      vencimientos: [
        ...(form.fechaVencimiento1
          ? [
              {
                numero: 1,
                fecha:
                  form.fechaVencimiento1,
                importe:
                  n(
                    form.importeVencimiento1 ||
                      form.total
                  ),
              },
            ]
          : []),

        ...(form.fechaVencimiento2
          ? [
              {
                numero: 2,
                fecha:
                  form.fechaVencimiento2,
                importe:
                  n(
                    form.importeVencimiento2 ||
                      form.total
                  ),
              },
            ]
          : []),
      ],
    };

    setSaving(true);

    try {
      const respuesta =
        editando
          ? await editarResumenPeaje(
              resumen._id,
              payload
            )
          : await crearResumenPeaje(
              payload
            );

      if (!respuesta?.ok) {
        setError(
          respuesta?.error ||
            "No se pudo guardar."
        );
        return;
      }

      if (
        typeof onSaved ===
        "function"
      ) {
        await onSaved(
          respuesta.resumen
        );
      }

      onHide();
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          "Error al guardar el resumen."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      backdrop="static"
    >
      <Form
        onSubmit={guardar}
      >
        <Modal.Header
          closeButton
        >
          <Modal.Title>
            {editando
              ? "Editar factura / resumen de peaje"
              : "Nueva factura / resumen de peaje"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}

          <Row>
            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Proveedor
              </Form.Label>

              <Form.Select
                name="proveedor"
                value={
                  form.proveedor
                }
                onChange={
                  handleChange
                }
              >
                <option value="TELEPASE">
                  TelePASE
                </option>

                <option value="TELEPEAJE_PLUS">
                  Telepeaje Plus
                </option>
              </Form.Select>
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Tipo
              </Form.Label>

              <Form.Select
                name="tipoDocumento"
                value={
                  form.tipoDocumento
                }
                onChange={
                  handleChange
                }
              >
                <option value="FACTURA">
                  Factura
                </option>

                <option value="RESUMEN_CUENTA">
                  Resumen de cuenta
                </option>
              </Form.Select>
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                N° documento
              </Form.Label>

              <Form.Control
                name="nroDocumento"
                value={
                  form.nroDocumento
                }
                onChange={
                  handleChange
                }
                required
              />
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Concesionario
              </Form.Label>

              <Form.Control
                name="concesionario"
                value={
                  form.concesionario
                }
                onChange={
                  handleChange
                }
                placeholder={
                  form.proveedor ===
                  "TELEPASE"
                    ? "Ej: AUSA"
                    : "Opcional"
                }
              />
            </Col>
          </Row>

          <Row>
            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Fecha emisión
              </Form.Label>

              <Form.Control
                type="date"
                name="fechaEmision"
                value={
                  form.fechaEmision
                }
                onChange={
                  handleChange
                }
              />
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Período desde
              </Form.Label>

              <Form.Control
                type="date"
                name="periodoDesde"
                value={
                  form.periodoDesde
                }
                onChange={
                  handleChange
                }
              />
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Período hasta
              </Form.Label>

              <Form.Control
                type="date"
                name="periodoHasta"
                value={
                  form.periodoHasta
                }
                onChange={
                  handleChange
                }
              />
            </Col>
            <Col md={3} className="mb-3">
              <Form.Label>
                1° vencimiento
              </Form.Label>

              <Form.Control
                type="date"
                name="fechaVencimiento1"
                value={
                  form.fechaVencimiento1
                }
                onChange={
                  handleChange
                }
              />
            </Col>

            <Col md={3} className="mb-3">
              <Form.Label>
                Importe 1° vto.
              </Form.Label>

              <Form.Control
                type="number"
                step="0.01"
                name="importeVencimiento1"
                value={
                  form.importeVencimiento1
                }
                onChange={
                  handleChange
                }
              />
            </Col>

            <Col md={3} className="mb-3">
              <Form.Label>
                2° vencimiento
              </Form.Label>

              <Form.Control
                type="date"
                name="fechaVencimiento2"
                value={
                  form.fechaVencimiento2
                }
                onChange={
                  handleChange
                }
              />
            </Col>

            <Col md={3} className="mb-3">
              <Form.Label>
                Importe 2° vto.
              </Form.Label>

              <Form.Control
                type="number"
                step="0.01"
                name="importeVencimiento2"
                value={
                  form.importeVencimiento2
                }
                onChange={
                  handleChange
                }
              />
            </Col>
          </Row>

          <hr />

          <Row>
            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label>
                Consumos (IVA Incluido)
              </Form.Label>

              <Form.Control
                type="number"
                step="0.01"
                name="subtotalConsumos"
                value={
                  form.subtotalConsumos
                }
                onChange={
                  handleChange
                }
              />
            </Col>

            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label>
                Impuestos /
                percepciones adicionales
              </Form.Label>

              <Form.Control
                type="number"
                step="0.01"
                name="impuestosPercepciones"
                value={
                  form.impuestosPercepciones
                }
                onChange={
                  handleChange
                }
              />
            </Col>

            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label>
                Otros conceptos
              </Form.Label>

              <Form.Control
                type="number"
                step="0.01"
                name="otrosConceptos"
                value={
                  form.otrosConceptos
                }
                onChange={
                  handleChange
                }
              />
            </Col>
          </Row>

          <Row>
            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label>
                Saldo anterior
              </Form.Label>

              <Form.Control
                type="number"
                step="0.01"
                name="saldoAnterior"
                value={
                  form.saldoAnterior
                }
                onChange={
                  handleChange
                }
              />
            </Col>

            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label>
                Pagos período
              </Form.Label>

              <Form.Control
                type="number"
                step="0.01"
                name="pagosPeriodo"
                value={
                  form.pagosPeriodo
                }
                onChange={
                  handleChange
                }
              />
            </Col>

            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label>
                Saldo pendiente
                anterior
              </Form.Label>

              <Form.Control
                type="number"
                step="0.01"
                name="saldoPendienteAnterior"
                value={
                  form.saldoPendienteAnterior
                }
                onChange={
                  handleChange
                }
              />
            </Col>
          </Row>

          <Row
            className="align-items-end"
          >
            <Col
              md={8}
              className="mb-3"
            >
              <Form.Label>
                Observaciones
              </Form.Label>

              <Form.Control
                name="observaciones"
                value={
                  form.observaciones
                }
                onChange={
                  handleChange
                }
              />
            </Col>

            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label
                className="fw-bold"
              >
                TOTAL
              </Form.Label>

              <div
                className="d-flex gap-2"
              >
                <Form.Control
                  type="number"
                  step="0.01"
                  name="total"
                  value={
                    form.total
                  }
                  onChange={
                    handleChange
                  }
                  required
                  style={{
                    fontWeight:
                      "bold",
                    fontSize:
                      "1.1rem",
                  }}
                />

                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={
                    calcularTotal
                  }
                >
                  Calcular
                </Button>
              </div>

              <div
                className="text-end fw-bold mt-1"
              >
                {money(
                  form.total
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={saving}
          >
            Cancelar
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving
              ? "Guardando..."
              : "Guardar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/* =========================================================
   MODAL VINCULACIÓN DE CONSUMOS
========================================================= */

function ModalConsumos({
  show,
  onHide,
  resumen,
  onSaved,
}) {
  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    consumos,
    setConsumos,
  ] = useState([]);

  const [
    seleccionados,
    setSeleccionados,
  ] = useState(
    new Set()
  );

  const [patente, setPatente] =
    useState("");

  const cargar =
    useCallback(async () => {
      if (
        !show ||
        !resumen?._id
      ) {
        return;
      }

      setLoading(true);
      setError("");

      try {
        const [
          disponibles,
          vinculados,
        ] =
          await Promise.all([
            getConsumosDisponiblesPeajes(
              {
                resumenId:
                  resumen._id,

                desde:
                  fechaInput(
                    resumen.periodoDesde
                  ),

                hasta:
                  fechaInput(
                    resumen.periodoHasta
                  ),
              }
            ),

            getConsumosResumenPeaje(
              resumen._id
            ),
          ]);

        if (
          disponibles?.ok ===
          false
        ) {
          throw new Error(
            disponibles.error
          );
        }

        if (
          vinculados?.ok ===
          false
        ) {
          throw new Error(
            vinculados.error
          );
        }

        const lista =
          Array.isArray(
            disponibles?.consumos
          )
            ? disponibles.consumos
            : [];

        setConsumos(lista);

        setSeleccionados(
          new Set(
            (
              vinculados?.consumos ||
              []
            ).map(
              (item) =>
                String(
                  item._id
                )
            )
          )
        );
      } catch (err) {
        console.error(err);

        setError(
          err?.message ||
            "No se pudieron cargar los consumos."
        );
      } finally {
        setLoading(false);
      }
    }, [
      show,
      resumen,
    ]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const visibles =
    useMemo(() => {
      const buscar =
        String(
          patente || ""
        )
          .trim()
          .toUpperCase();

      if (!buscar) {
        return consumos;
      }

      return consumos.filter(
        (item) =>
          String(
            item.patente ||
              ""
          )
            .toUpperCase()
            .includes(buscar)
      );
    }, [
      consumos,
      patente,
    ]);

  const seleccionadosLista =
    useMemo(
      () =>
        consumos.filter(
          (item) =>
            seleccionados.has(
              String(
                item._id
              )
            )
        ),
      [
        consumos,
        seleccionados,
      ]
    );

  const totalSeleccionado =
    useMemo(
      () =>
        Number(
          seleccionadosLista
            .reduce(
              (acc, item) =>
                acc +
                n(
                  item.importeFinal
                ),
              0
            )
            .toFixed(2)
        ),
      [seleccionadosLista]
    );

  const diferencia =
    Number(
      (
        totalSeleccionado -
        n(
          resumen?.subtotalConsumos
        )
      ).toFixed(2)
    );

  const toggle = (id) => {
    setSeleccionados(
      (prev) => {
        const next =
          new Set(prev);

        const key =
          String(id);

        if (
          next.has(key)
        ) {
          next.delete(key);
        } else {
          next.add(key);
        }

        return next;
      }
    );
  };

  const seleccionarVisibles =
    () => {
      setSeleccionados(
        (prev) => {
          const next =
            new Set(prev);

          visibles.forEach(
            (item) => {
              next.add(
                String(
                  item._id
                )
              );
            }
          );

          return next;
        }
      );
    };

  const limpiar =
    () => {
      setSeleccionados(
        new Set()
      );
    };

  const guardar = async () => {
    setSaving(true);
    setError("");

    try {
      const respuesta =
        await vincularConsumosResumenPeaje(
          resumen._id,
          Array.from(
            seleccionados
          )
        );

      if (!respuesta?.ok) {
        setError(
          respuesta?.error ||
            "No se pudo guardar la selección."
        );

        return;
      }

      if (
        typeof onSaved ===
        "function"
      ) {
        await onSaved();
      }

      onHide();
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          "Error al vincular consumos."
      );
    } finally {
      setSaving(false);
    }
  };

  const actualizarSubtotal =
    async () => {
      if (
        !window.confirm(
          `¿Actualizar el subtotal de la factura a ${money(
            totalSeleccionado
          )}?`
        )
      ) {
        return;
      }

      setSaving(true);
      setError("");

      try {
        const respuesta =
          await editarResumenPeaje(
            resumen._id,
            {
              subtotalConsumos:
                totalSeleccionado,
            }
          );

        if (!respuesta?.ok) {
          setError(
            respuesta?.error ||
              "No se pudo actualizar el subtotal."
          );
          return;
        }

        if (
          typeof onSaved ===
          "function"
        ) {
          await onSaved();
        }
      } finally {
        setSaving(false);
      }
    };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      backdrop="static"
    >
      <Modal.Header
        closeButton
      >
        <Modal.Title>
          Vincular consumos
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div
          className="mb-3 p-3 border rounded bg-light"
        >
          <Row>
            <Col md={3}>
              <strong>
                Proveedor
              </strong>
              <div>
                {
                  resumen?.proveedor
                }
              </div>
            </Col>

            <Col md={3}>
              <strong>
                Documento
              </strong>
              <div>
                {
                  resumen?.nroDocumento
                }
              </div>
            </Col>

            <Col md={3}>
              <strong>
                Período
              </strong>
              <div>
                {fechaCorta(
                  resumen?.periodoDesde
                )}{" "}
                al{" "}
                {fechaCorta(
                  resumen?.periodoHasta
                )}
              </div>
            </Col>

            <Col md={3}>
              <strong>
                Subtotal factura
              </strong>

              <div>
                {money(
                  resumen?.subtotalConsumos
                )}
              </div>
            </Col>
          </Row>
        </div>

        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <Row
          className="mb-3 align-items-end"
        >
          <Col md={4}>
            <Form.Label>
              Buscar patente
            </Form.Label>

            <Form.Control
              value={patente}
              onChange={(e) =>
                setPatente(
                  e.target.value
                )
              }
              placeholder="Ej: AE818TJ"
            />
          </Col>

          <Col md={8}>
            <div
              className="d-flex gap-2 flex-wrap"
            >
              <Button
                variant="outline-primary"
                onClick={
                  seleccionarVisibles
                }
              >
                Seleccionar visibles
              </Button>

              <Button
                variant="outline-secondary"
                onClick={
                  limpiar
                }
              >
                Limpiar selección
              </Button>

              <Button
                variant="outline-success"
                onClick={
                  actualizarSubtotal
                }
                disabled={
                  totalSeleccionado <=
                  0
                }
              >
                Usar total seleccionado como subtotal
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={3}>
            <div
              className="border rounded p-2"
            >
              <small>
                Seleccionados
              </small>

              <div
                className="fw-bold fs-5"
              >
                {
                  seleccionados.size
                }
              </div>
            </div>
          </Col>

          <Col md={3}>
            <div
              className="border rounded p-2"
            >
              <small>
                Total consumos
              </small>

              <div
                className="fw-bold fs-5"
              >
                {money(
                  totalSeleccionado
                )}
              </div>
            </div>
          </Col>

          <Col md={3}>
            <div
              className="border rounded p-2"
            >
              <small>
                Subtotal factura
              </small>

              <div
                className="fw-bold fs-5"
              >
                {money(
                  resumen?.subtotalConsumos
                )}
              </div>
            </div>
          </Col>

          <Col md={3}>
            <div
              className="border rounded p-2"
            >
              <small>
                Diferencia
              </small>

              <div
                className={`fw-bold fs-5 ${
                  Math.abs(
                    diferencia
                  ) <= 0.01
                    ? "text-success"
                    : "text-danger"
                }`}
              >
                {money(
                  diferencia
                )}
              </div>
            </div>
          </Col>
        </Row>

        {loading ? (
          <div
            className="text-center py-5"
          >
            <Spinner animation="border" />
          </div>
        ) : (
          <div
            style={{
              maxHeight:
                "430px",
              overflowY:
                "auto",
            }}
          >
            <Table
              striped
              bordered
              hover
              responsive
              size="sm"
              className="mb-0"
            >
              <thead
                style={{
                  position:
                    "sticky",
                  top: 0,
                  background:
                    "white",
                  zIndex: 2,
                }}
              >
                <tr>
                  <th
                    style={{
                      width:
                        "50px",
                    }}
                  />

                  <th>
                    Fecha
                  </th>

                  <th>
                    Hora
                  </th>

                  <th>
                    Patente
                  </th>

                  <th>
                    Concesionario
                  </th>

                  <th>
                    Estación
                  </th>

                  <th>
                    Vía
                  </th>

                  <th className="text-end">
                    Importe
                  </th>
                </tr>
              </thead>

              <tbody>
                {visibles.map(
                  (item) => {
                    const checked =
                      seleccionados.has(
                        String(
                          item._id
                        )
                      );

                    return (
                      <tr
                        key={
                          item._id
                        }
                        onClick={() =>
                          toggle(
                            item._id
                          )
                        }
                        style={{
                          cursor:
                            "pointer",
                        }}
                      >
                        <td
                          className="text-center"
                        >
                          <Form.Check
                            checked={
                              checked
                            }
                            onChange={() =>
                              toggle(
                                item._id
                              )
                            }
                            onClick={(
                              e
                            ) =>
                              e.stopPropagation()
                            }
                          />
                        </td>

                        <td>
                          {fechaCorta(
                            item.fecha
                          )}
                        </td>

                        <td>
                          {item.hora ||
                            "-"}
                        </td>

                        <td
                          className="fw-bold"
                        >
                          {getVehiculo(
                            item
                          )}
                        </td>

                        <td>
                          {item.concesionario ||
                            "-"}
                        </td>

                        <td>
                          {item.estacion ||
                            "-"}
                        </td>

                        <td>
                          {item.via ||
                            "-"}
                        </td>

                        <td className="text-end fw-bold">
                          {money(
                            item.importeFinal
                          )}
                        </td>
                      </tr>
                    );
                  }
                )}

                {visibles.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center py-4 text-muted"
                    >
                      No hay consumos disponibles.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={saving}
        >
          Cancelar
        </Button>

        <Button
          variant="primary"
          onClick={guardar}
          disabled={
            saving ||
            loading
          }
        >
          {saving
            ? "Guardando..."
            : "Guardar selección"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* =========================================================
   MODAL DE PAGO
========================================================= */

function ModalPago({
  show,
  onHide,
  resumen,
  onSaved,
}) {
  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [cajas, setCajas] =
    useState([]);

  const [cuentas, setCuentas] =
    useState([]);

  const [
    cartera,
    setCartera,
  ] = useState([]);

  const [form, setForm] =
    useState({
      medioPago: "CAJA",

      fechaPago:
        hoyInput(),

      importePago: "",

      caja: "",

      cuentaBancaria: "",

      usarCartera:
        true,

      carteraItem: "",

      nroCheque: "",

      idICheq: "",

      banco: "",

      fechaCheque: "",

      fechaLiquidacion:
        "",
    });

  useEffect(() => {
    if (!show) return;

    setError("");

    setForm({
      medioPago: "CAJA",

      fechaPago:
        hoyInput(),

      importePago:
        n(resumen?.total),

      caja: "",

      cuentaBancaria: "",

      usarCartera:
        true,

      carteraItem: "",

      nroCheque: "",

      idICheq: "",

      banco: "",

      fechaCheque: "",

      fechaLiquidacion:
        "",
    });

    setLoading(true);

    Promise.all([
      getCajas(),
      getCuentas(),
      getCarteraDisponibles({
        estados: [
          "EN_CARTERA",
        ],
      }),
    ])
      .then(
        ([
          rCajas,
          rCuentas,
          rCartera,
        ]) => {
          setCajas(
            Array.isArray(
              rCajas
            )
              ? rCajas
              : rCajas?.cajas ||
                  []
          );

          setCuentas(
            Array.isArray(
              rCuentas
            )
              ? rCuentas
              : rCuentas?.cuentas ||
                  []
          );

          setCartera(
            normalizarItemsCartera(
              rCartera
            )
          );
        }
      )
      .catch((err) => {
        console.error(err);

        setError(
          "No se pudieron cargar Caja/Banco/Cartera."
        );
      })
      .finally(() =>
        setLoading(false)
      );
  }, [show]);

  const carteraFiltrada =
    useMemo(() => {
      if (
        form.medioPago ===
        "CHEQUE"
      ) {
        return cartera.filter(
          (item) =>
            item.tipo ===
            "CHEQUE"
        );
      }

      if (
        form.medioPago ===
        "ECHEQ"
      ) {
        return cartera.filter(
          (item) =>
            item.tipo ===
            "ICHEQ"
        );
      }

      return [];
    }, [
      cartera,
      form.medioPago,
    ]);

  const opcionesPago =
    useMemo(() => {
      const opciones = [];

      const agregar = (
        importe,
        texto
      ) => {
        const valor =
          n(importe);

        if (valor <= 0) {
          return;
        }

        const yaExiste =
          opciones.some(
            (item) =>
              Math.abs(
                item.importe -
                  valor
              ) <= 0.01
          );

        if (!yaExiste) {
          opciones.push({
            importe: valor,
            texto,
          });
        }
      };

      agregar(
        resumen?.total,
        "Total factura"
      );

      (
        resumen?.vencimientos ||
        []
      ).forEach(
        (item, index) => {
          agregar(
            item.importe,
            `${index + 1}° vencimiento - ${fechaCorta(
              item.fecha
            )}`
          );
        }
      );

      return opciones;
    }, [resumen]);  

  const change = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        type ===
        "checkbox"
          ? checked
          : value,
    }));
  };

  const guardar = async () => {
    setError("");

    if (
      !resumen?._id
    ) {
      return;
    }

    const payload = {
      medioPago:
        form.medioPago,

      fechaPago:
        form.fechaPago,

      importe:
        n(form.importePago),
    };

    if (
      form.medioPago ===
      "CAJA"
    ) {
      if (!form.caja) {
        setError(
          "Seleccione una caja."
        );

        return;
      }

      payload.caja =
        form.caja;
    }

    if (
      form.medioPago ===
      "CUENTA_BANCARIA"
    ) {
      if (
        !form.cuentaBancaria
      ) {
        setError(
          "Seleccione una cuenta bancaria."
        );

        return;
      }

      payload.cuentaBancaria =
        form.cuentaBancaria;
    }

    if (
      form.medioPago ===
        "CHEQUE" ||
      form.medioPago ===
        "ECHEQ"
    ) {
      if (
        form.usarCartera
      ) {
        if (
          !form.carteraItem
        ) {
          setError(
            "Seleccione un valor de cartera."
          );

          return;
        }

        payload.carteraItem =
          form.carteraItem;
      } else if (
        form.medioPago ===
        "CHEQUE"
      ) {
        if (
          !form.nroCheque.trim()
        ) {
          setError(
            "Ingrese el número de cheque."
          );
          return;
        }

        payload.nroCheque =
          form.nroCheque;

        payload.banco =
          form.banco;

        payload.fechaCheque =
          form.fechaCheque ||
          form.fechaPago;
      } else {
        if (
          !form.idICheq.trim()
        ) {
          setError(
            "Ingrese el identificador del eCheq."
          );
          return;
        }

        payload.idICheq =
          form.idICheq;

        payload.banco =
          form.banco;

        payload.fechaLiquidacion =
          form.fechaLiquidacion ||
          form.fechaPago;
      }
    }

    const confirmar =
      window.confirm(
        `¿Registrar el pago de ${money(
          form.importePago
        )} correspondiente a ${resumen.nroDocumento}?`
      );

    if (!confirmar) {
      return;
    }

    setSaving(true);

    try {
      const respuesta =
        await pagarResumenPeaje(
          resumen._id,
          payload
        );

      if (!respuesta?.ok) {
        setError(
          respuesta?.error ||
            "No se pudo registrar el pago."
        );

        return;
      }

      if (
        typeof onSaved ===
        "function"
      ) {
        await onSaved();
      }

      onHide();
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          "Error al registrar el pago."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      backdrop="static"
    >
      <Modal.Header
        closeButton
      >
        <Modal.Title>
          Registrar pago
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <div
          className="border rounded p-3 mb-3 bg-light"
        >
          <Row>
            <Col md={6}>
              <strong>
                Documento
              </strong>

              <div>
                {resumen?.proveedor}{" "}
                -{" "}
                {
                  resumen?.nroDocumento
                }
              </div>
            </Col>

            <Col
              md={6}
              className="text-md-end"
            >
              <strong>
                Total factura
              </strong>

              <div className="fs-4 fw-bold">
                {money(
                  resumen?.total
                )}
              </div>
            </Col>
          </Row>
        </div>

        {loading ? (
          <div
            className="text-center py-4"
          >
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <Row>
              <Col
                md={6}
                className="mb-3"
              >
                <Form.Label>
                  Fecha de pago
                </Form.Label>

                <Form.Control
                  type="date"
                  name="fechaPago"
                  value={
                    form.fechaPago
                  }
                  onChange={
                    change
                  }
                />
              </Col>

              <Col
                md={6}
                className="mb-3"
              >
                <Form.Label>
                  Medio de pago
                </Form.Label>

                <Form.Select
                  name="medioPago"
                  value={
                    form.medioPago
                  }
                  onChange={
                    change
                  }
                >
                  <option value="CAJA">
                    Efectivo / Caja
                  </option>

                  <option value="CUENTA_BANCARIA">
                    Banco / débito automático
                  </option>

                  <option value="CHEQUE">
                    Cheque
                  </option>

                  <option value="ECHEQ">
                    eCheq
                  </option>
                </Form.Select>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                Importe a cancelar
              </Form.Label>

              <Form.Select
                name="importePago"
                value={
                  form.importePago
                }
                onChange={
                  change
                }
              >
                {opcionesPago.map(
                  (item) => (
                    <option
                      key={
                        item.importe
                      }
                      value={
                        item.importe
                      }
                    >
                      {item.texto} -{" "}
                      {money(
                        item.importe
                      )}
                    </option>
                  )
                )}
              </Form.Select>
            </Form.Group>

            {form.medioPago ===
              "CAJA" && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Caja
                </Form.Label>

                <Form.Select
                  name="caja"
                  value={
                    form.caja
                  }
                  onChange={
                    change
                  }
                >
                  <option value="">
                    Seleccione...
                  </option>

                  {cajas.map(
                    (item) => (
                      <option
                        key={
                          item._id
                        }
                        value={
                          item._id
                        }
                      >
                        {getCajaLabel(
                          item
                        )}{" "}
                        - Saldo:{" "}
                        {money(
                          item.saldoActual
                        )}
                      </option>
                    )
                  )}
                </Form.Select>
              </Form.Group>
            )}

            {form.medioPago ===
              "CUENTA_BANCARIA" && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Cuenta bancaria
                </Form.Label>

                <Form.Select
                  name="cuentaBancaria"
                  value={
                    form.cuentaBancaria
                  }
                  onChange={
                    change
                  }
                >
                  <option value="">
                    Seleccione...
                  </option>

                  {cuentas.map(
                    (item) => (
                      <option
                        key={
                          item._id
                        }
                        value={
                          item._id
                        }
                      >
                        {getCuentaLabel(
                          item
                        )}{" "}
                        - Saldo:{" "}
                        {money(
                          item.saldoActual
                        )}
                      </option>
                    )
                  )}
                </Form.Select>
              </Form.Group>
            )}

            {(form.medioPago ===
              "CHEQUE" ||
              form.medioPago ===
                "ECHEQ") && (
              <>
                <Form.Check
                  type="switch"
                  className="mb-3"
                  name="usarCartera"
                  checked={
                    form.usarCartera
                  }
                  onChange={
                    change
                  }
                  label="Usar un cheque/eCheq que ya está en cartera"
                />

                {form.usarCartera ? (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Valor disponible
                    </Form.Label>

                    <Form.Select
                      name="carteraItem"
                      value={
                        form.carteraItem
                      }
                      onChange={
                        change
                      }
                    >
                      <option value="">
                        Seleccione...
                      </option>

                      {carteraFiltrada.map(
                        (
                          item
                        ) => (
                          <option
                            key={
                              item._id
                            }
                            value={
                              item._id
                            }
                          >
                            {item.nro ||
                              item.nroCheque ||
                              item.idICheq ||
                              "Sin número"}{" "}
                            -{" "}
                            {money(
                              item.importe
                            )}{" "}
                            -{" "}
                            {item.bancoEmisor ||
                              item.banco ||
                              ""}
                          </option>
                        )
                      )}
                    </Form.Select>

                    <small className="text-muted">
                      El backend solamente permitirá utilizar un valor cuyo importe coincida exactamente con el pago.
                    </small>
                  </Form.Group>
                ) : (
                  <>
                    {form.medioPago ===
                      "CHEQUE" && (
                      <Row>
                        <Col
                          md={6}
                          className="mb-3"
                        >
                          <Form.Label>
                            N° cheque
                          </Form.Label>

                          <Form.Control
                            name="nroCheque"
                            value={
                              form.nroCheque
                            }
                            onChange={
                              change
                            }
                          />
                        </Col>

                        <Col
                          md={6}
                          className="mb-3"
                        >
                          <Form.Label>
                            Fecha cheque
                          </Form.Label>

                          <Form.Control
                            type="date"
                            name="fechaCheque"
                            value={
                              form.fechaCheque
                            }
                            onChange={
                              change
                            }
                          />
                        </Col>
                      </Row>
                    )}

                    {form.medioPago ===
                      "ECHEQ" && (
                      <Row>
                        <Col
                          md={6}
                          className="mb-3"
                        >
                          <Form.Label>
                            ID eCheq
                          </Form.Label>

                          <Form.Control
                            name="idICheq"
                            value={
                              form.idICheq
                            }
                            onChange={
                              change
                            }
                          />
                        </Col>

                        <Col
                          md={6}
                          className="mb-3"
                        >
                          <Form.Label>
                            Fecha liquidación
                          </Form.Label>

                          <Form.Control
                            type="date"
                            name="fechaLiquidacion"
                            value={
                              form.fechaLiquidacion
                            }
                            onChange={
                              change
                            }
                          />
                        </Col>
                      </Row>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Banco
                      </Form.Label>

                      <Form.Control
                        name="banco"
                        value={
                          form.banco
                        }
                        onChange={
                          change
                        }
                      />
                    </Form.Group>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={saving}
        >
          Cancelar
        </Button>

        <Button
          variant="success"
          onClick={guardar}
          disabled={
            saving ||
            loading
          }
        >
          <FaMoneyBillWave className="me-1" />

          {saving
            ? "Registrando..."
            : "Registrar pago"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* =========================================================
   PANTALLA PRINCIPAL
========================================================= */

export default function ResumenesPeajes() {
  const [
    resumenes,
    setResumenes,
  ] = useState([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    filtroProveedor,
    setFiltroProveedor,
  ] = useState("");

  const [
    filtroEstado,
    setFiltroEstado,
  ] = useState("");

  const [
    modalResumen,
    setModalResumen,
  ] = useState({
    show: false,
    resumen: null,
  });

  const [
    modalConsumos,
    setModalConsumos,
  ] = useState({
    show: false,
    resumen: null,
  });

  const [
    modalPago,
    setModalPago,
  ] = useState({
    show: false,
    resumen: null,
  });

  const cargar =
    useCallback(async () => {
      setLoading(true);
      setError("");

      try {
        const params = {};

        if (
          filtroProveedor
        ) {
          params.proveedor =
            filtroProveedor;
        }

        if (filtroEstado) {
          params.estado =
            filtroEstado;
        }

        const respuesta =
          await getResumenesPeajes(
            params
          );

        if (!respuesta?.ok) {
          setError(
            respuesta?.error ||
              "No se pudieron cargar los resúmenes."
          );

          setResumenes([]);

          return;
        }

        setResumenes(
          Array.isArray(
            respuesta.resumenes
          )
            ? respuesta.resumenes
            : []
        );
      } catch (err) {
        console.error(err);

        setError(
          "Error cargando resúmenes."
        );

        setResumenes([]);
      } finally {
        setLoading(false);
      }
    }, [
      filtroProveedor,
      filtroEstado,
    ]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const totalPendiente =
    useMemo(
      () =>
        resumenes
          .filter(
            (item) =>
              item.estado ===
              "PENDIENTE"
          )
          .reduce(
            (acc, item) =>
              acc +
              n(item.total),
            0
          ),
      [resumenes]
    );

  const cantidadPendientes =
    resumenes.filter(
      (item) =>
        item.estado ===
        "PENDIENTE"
    ).length;

  const anularPago =
    async (resumen) => {
      if (
        !window.confirm(
          `¿Seguro que desea ANULAR el pago de ${resumen.nroDocumento} por ${money(
            resumen.montoPagado ||
              resumen.total
          )}?\n\nEsta operación devolverá el saldo a Caja/Banco o revertirá Cartera.`
        )
      ) {
        return;
      }

      setLoading(true);

      try {
        const respuesta =
          await anularPagoResumenPeaje(
            resumen._id
          );

        if (!respuesta?.ok) {
          alert(
            respuesta?.error ||
              "No se pudo anular el pago."
          );
          return;
        }

        await cargar();
      } finally {
        setLoading(false);
      }
    };

  return (
    <div
      className="container-fluid py-3"
      style={{
        backgroundColor:
          "#fff",
        minHeight:
          "100vh",
      }}
    >
      {/* CABECERA */}

      <div
        className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
      >
        <div>
          <h3 className="mb-0">
            Resúmenes y pagos de peajes
          </h3>

          <small className="text-muted">
            Facturas, consumos asociados y pagos
          </small>
        </div>

        <div
          className="d-flex gap-2"
        >
          <Button
            variant="outline-secondary"
            onClick={cargar}
          >
            <FaSyncAlt className="me-1" />
            Actualizar
          </Button>

          <Button
            variant="primary"
            onClick={() =>
              setModalResumen({
                show: true,
                resumen: null,
              })
            }
          >
            <FaPlus className="me-1" />
            Nuevo resumen
          </Button>
        </div>
      </div>

      {/* KPIS */}

      <Row className="mb-3">
        <Col
          md={4}
          className="mb-2"
        >
          <div
            className="border rounded p-3"
          >
            <small className="text-muted">
              Documentos
            </small>

            <div
              className="fs-4 fw-bold"
            >
              {resumenes.length}
            </div>
          </div>
        </Col>

        <Col
          md={4}
          className="mb-2"
        >
          <div
            className="border rounded p-3"
          >
            <small className="text-muted">
              Pendientes
            </small>

            <div
              className="fs-4 fw-bold"
            >
              {cantidadPendientes}
            </div>
          </div>
        </Col>

        <Col
          md={4}
          className="mb-2"
        >
          <div
            className="border rounded p-3"
          >
            <small className="text-muted">
              Total pendiente
            </small>

            <div
              className="fs-4 fw-bold"
            >
              {money(
                totalPendiente
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* FILTROS */}

      <div
        className="border rounded p-3 mb-3"
      >
        <Row>
          <Col
            md={4}
            className="mb-2"
          >
            <Form.Label>
              Proveedor
            </Form.Label>

            <Form.Select
              value={
                filtroProveedor
              }
              onChange={(e) =>
                setFiltroProveedor(
                  e.target.value
                )
              }
            >
              <option value="">
                Todos
              </option>

              <option value="TELEPASE">
                TelePASE
              </option>

              <option value="TELEPEAJE_PLUS">
                Telepeaje Plus
              </option>
            </Form.Select>
          </Col>

          <Col
            md={4}
            className="mb-2"
          >
            <Form.Label>
              Estado
            </Form.Label>

            <Form.Select
              value={
                filtroEstado
              }
              onChange={(e) =>
                setFiltroEstado(
                  e.target.value
                )
              }
            >
              <option value="">
                Todos
              </option>

              <option value="PENDIENTE">
                Pendiente
              </option>

              <option value="PAGADO">
                Pagado
              </option>

              <option value="VENCIDO">
                Vencido
              </option>

              <option value="ANULADO">
                Anulado
              </option>
            </Form.Select>
          </Col>
        </Row>
      </div>

      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      {/* TABLA */}

      <div
        className="border rounded"
        style={{
          overflow:
            "hidden",
        }}
      >
        <Table
          striped
          hover
          responsive
          className="mb-0"
        >
          <thead
            style={{
              background:
                "#f8f9fa",
            }}
          >
            <tr>
              <th>
                Emisión
              </th>

              <th>
                Proveedor
              </th>

              <th>
                Documento
              </th>

              <th>
                Período
              </th>

              <th className="text-end">
                Consumos
              </th>

              <th className="text-end">
                Impuestos
              </th>

              <th className="text-end">
                Total
              </th>

              <th className="text-center">
                Estado
              </th>

              <th
                className="text-end"
                style={{
                  minWidth:
                    "300px",
                }}
              >
                Acciones
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="9"
                  className="text-center py-5"
                >
                  <Spinner animation="border" />
                </td>
              </tr>
            ) : resumenes.length ===
              0 ? (
              <tr>
                <td
                  colSpan="9"
                  className="text-center text-muted py-5"
                >
                  No hay resúmenes registrados.
                </td>
              </tr>
            ) : (
              resumenes.map(
                (item) => (
                  <tr
                    key={
                      item._id
                    }
                  >
                    <td>
                      {fechaCorta(
                        item.fechaEmision
                      )}
                    </td>

                    <td>
                      <strong>
                        {
                          item.proveedor
                        }
                      </strong>

                      {item.concesionario && (
                        <div>
                          <small className="text-muted">
                            {
                              item.concesionario
                            }
                          </small>
                        </div>
                      )}
                    </td>

                    <td>
                      {
                        item.nroDocumento
                      }
                    </td>

                    <td>
                      {fechaCorta(
                        item.periodoDesde
                      )}{" "}
                      -{" "}
                      {fechaCorta(
                        item.periodoHasta
                      )}
                    </td>

                    <td className="text-end">
                      {money(
                        item.subtotalConsumos
                      )}
                    </td>

                    <td className="text-end">
                      {money(
                        item.impuestosPercepciones
                      )}
                    </td>

                    <td className="text-end fw-bold">
                      {money(
                        item.total
                      )}
                    </td>

                    <td className="text-center">
                      <Badge
                        bg={estadoBadge(
                          item.estado
                        )}
                      >
                        {
                          item.estado
                        }
                      </Badge>
                    </td>

                    <td className="text-end">
                      <div
                        className="d-flex gap-1 justify-content-end flex-wrap"
                      >
                        {item.estado !==
                          "PAGADO" &&
                          item.estado !==
                            "ANULADO" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                title="Editar"
                                onClick={() =>
                                  setModalResumen(
                                    {
                                      show: true,
                                      resumen:
                                        item,
                                    }
                                  )
                                }
                              >
                                <FaEdit />
                              </Button>

                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() =>
                                  setModalConsumos(
                                    {
                                      show: true,
                                      resumen:
                                        item,
                                    }
                                  )
                                }
                              >
                                <FaLink className="me-1" />
                                Consumos
                              </Button>

                              <Button
                                size="sm"
                                variant="success"
                                onClick={() =>
                                  setModalPago(
                                    {
                                      show: true,
                                      resumen:
                                        item,
                                    }
                                  )
                                }
                              >
                                <FaMoneyBillWave className="me-1" />
                                Pagar
                              </Button>
                            </>
                          )}

                        {item.estado ===
                          "PAGADO" && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() =>
                              anularPago(
                                item
                              )
                            }
                          >
                            <FaUndoAlt className="me-1" />
                            Anular pago
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </Table>
      </div>

      {/* MODALES */}

      <ModalResumen
        show={
          modalResumen.show
        }
        resumen={
          modalResumen.resumen
        }
        onHide={() =>
          setModalResumen({
            show: false,
            resumen: null,
          })
        }
        onSaved={cargar}
      />

      <ModalConsumos
        show={
          modalConsumos.show
        }
        resumen={
          modalConsumos.resumen
        }
        onHide={() =>
          setModalConsumos({
            show: false,
            resumen: null,
          })
        }
        onSaved={cargar}
      />

      <ModalPago
        show={
          modalPago.show
        }
        resumen={
          modalPago.resumen
        }
        onHide={() =>
          setModalPago({
            show: false,
            resumen: null,
          })
        }
        onSaved={cargar}
      />
    </div>
  );
}